/**
 * Cookie consent storage & helpers for Powersure.
 *
 * Storage key: `powersure_cookie_consent`
 * Structure:
 *   {
 *     necessary: true,
 *     analytics: boolean,
 *     marketing: boolean,
 *     timestamp: ISO string,
 *     version: 1
 *   }
 */

export const STORAGE_KEY = 'powersure_cookie_consent';
export const CONSENT_VERSION = 1;

const listeners = new Set();

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasConsent() {
  return getConsent() !== null;
}

export function setConsent({ analytics, marketing }) {
  const value = {
    necessary: true,
    analytics: Boolean(analytics),
    marketing: Boolean(marketing),
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* localStorage may be disabled */
  }
  applyConsent(value);
  listeners.forEach((fn) => {
    try { fn(value); } catch {}
  });
  return value;
}

/**
 * Push the current consent into Google Consent Mode v2 and load PostHog
 * if analytics is granted. Idempotent.
 */
export function applyConsent(consent) {
  const c = consent || getConsent();
  if (!c) return;

  // Google Consent Mode v2 — works for gtag.js loaded in index.html
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: c.marketing ? 'granted' : 'denied',
        ad_user_data: c.marketing ? 'granted' : 'denied',
        ad_personalization: c.marketing ? 'granted' : 'denied',
        analytics_storage: c.analytics ? 'granted' : 'denied',
      });
    }
  } catch {}

  // PostHog — only initialise after analytics consent
  try {
    if (typeof window !== 'undefined') {
      if (c.analytics && typeof window.__pwLoadPostHog === 'function') {
        window.__pwLoadPostHog();
        if (window.posthog && typeof window.posthog.opt_in_capturing === 'function') {
          window.posthog.opt_in_capturing();
        }
      } else if (!c.analytics && window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
        window.posthog.opt_out_capturing();
      }
    }
  } catch {}
}

export function onConsentChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Apply consent on page load. Should be called once at app start.
 */
export function bootstrapConsent() {
  const c = getConsent();
  if (c) applyConsent(c);
}
